// AidFone Multi-Language Configuration
// Supporting 50+ languages with cultural adaptation

import i18n from 'i18next';
import { initReactI18next } from 'react-i18next';
import type { CulturalConfig, Language } from '../types';
import { localeFromPath } from './path-locale';

// Import translation files
import enTranslations from './locales/en.json';
import frTranslations from './locales/fr.json';
import esTranslations from './locales/es.json';
import arTranslations from './locales/ar.json';
import zhTranslations from './locales/zh.json';
import hiTranslations from './locales/hi.json';
import ptTranslations from './locales/pt.json';
import ruTranslations from './locales/ru.json';
import jaTranslations from './locales/ja.json';
import deTranslations from './locales/de.json';

// Language detection — addendum Risk 1 ("deterministic locale at first
// render"). On the prerendered marketing surface (/ and /fr), the URL
// path is the ONLY source of truth — never localStorage, never
// navigator.language. This prevents the FR/EN flicker that kills SEO
// + caregiver trust in Quebec.
//
// Authenticated dashboard surfaces (/dashboard, /account, etc.) aren't
// prerendered, so localStorage fallback is OK there — those users have
// logged in and have explicit language preferences.
//
// This function runs synchronously at module load, BEFORE React mounts,
// so i18n.init({ lng }) is set before any <SEO> or i18n hook reads it.
//
// Marketing routes that must be URL-is-law. Adding a route here also
// requires adding it to scripts/prerender.js ROUTES.
const MARKETING_EN_PATHS = new Set(['/', '/about', '/contact', '/faq', '/delete-account']);

const detectUserLanguage = (): string => {
  if (typeof window !== 'undefined' && window.location) {
    const path = window.location.pathname;
    // Marketing surface — URL is law. Never check localStorage here.
    if (path === '/fr' || path.startsWith('/fr/')) {
      return 'fr';
    }
    // '/faq/<slug>' detail pages are prerendered English but were not listed,
    // so a visitor with a stored 'fr' preference rendered French copy under an
    // English canonical URL.
    if (path === '' || MARKETING_EN_PATHS.has(path) || path.startsWith('/faq/')) {
      return 'en';
    }
  }

  // Non-marketing surfaces: stored preference > browser locale > Loi 101
  // default. Safe because these routes are interactive and not crawled.
  const stored = typeof localStorage !== 'undefined' ? localStorage.getItem('aidfone_language') : null;
  if (stored) return stored;

  const browserLang = (typeof navigator !== 'undefined' && (navigator.language || (navigator as Navigator & { userLanguage?: string }).userLanguage)) || 'fr';
  const code = browserLang.split('-')[0];
  return code || 'fr';
};

// Cultural settings by language
export const culturalConfig: Record<string, CulturalConfig> = {
  en: {
    direction: 'ltr',
    dateFormat: 'MM/DD/YYYY',
    timeFormat: '12h',
    firstDayOfWeek: 0, // Sunday
    decimalSeparator: '.',
    thousandsSeparator: ',',
    currency: 'USD',
    currencyPosition: 'before',
    phoneFormat: '+1 (XXX) XXX-XXXX',
  },
  fr: {
    direction: 'ltr',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    firstDayOfWeek: 1, // Monday
    decimalSeparator: ',',
    thousandsSeparator: ' ',
    currency: 'EUR',
    currencyPosition: 'after',
    phoneFormat: '+33 X XX XX XX XX',
  },
  es: {
    direction: 'ltr',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    firstDayOfWeek: 1,
    decimalSeparator: ',',
    thousandsSeparator: '.',
    currency: 'EUR',
    currencyPosition: 'after',
    phoneFormat: '+34 XXX XX XX XX',
  },
  ar: {
    direction: 'rtl',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    firstDayOfWeek: 6, // Saturday
    decimalSeparator: '\u066B',
    thousandsSeparator: '\u066C',
    currency: 'SAR',
    currencyPosition: 'after',
    phoneFormat: '+966 XX XXX XXXX',
    numerals: '\u0660\u0661\u0662\u0663\u0664\u0665\u0666\u0667\u0668\u0669', // Arabic-Indic numerals
  },
  zh: {
    direction: 'ltr',
    dateFormat: 'YYYY-MM-DD',
    timeFormat: '24h',
    firstDayOfWeek: 1,
    decimalSeparator: '.',
    thousandsSeparator: ',',
    currency: 'CNY',
    currencyPosition: 'before',
    phoneFormat: '+86 XXX XXXX XXXX',
  },
  hi: {
    direction: 'ltr',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '12h',
    firstDayOfWeek: 1,
    decimalSeparator: '.',
    thousandsSeparator: ',',
    currency: 'INR',
    currencyPosition: 'before',
    phoneFormat: '+91 XXXXX XXXXX',
    numerals: '\u0966\u0967\u0968\u0969\u096A\u096B\u096C\u096D\u096E\u096F', // Devanagari numerals
  },
  pt: {
    direction: 'ltr',
    dateFormat: 'DD/MM/YYYY',
    timeFormat: '24h',
    firstDayOfWeek: 1,
    decimalSeparator: ',',
    thousandsSeparator: '.',
    currency: 'BRL',
    currencyPosition: 'before',
    phoneFormat: '+55 (XX) XXXXX-XXXX',
  },
  ru: {
    direction: 'ltr',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    firstDayOfWeek: 1,
    decimalSeparator: ',',
    thousandsSeparator: ' ',
    currency: 'RUB',
    currencyPosition: 'after',
    phoneFormat: '+7 (XXX) XXX-XX-XX',
  },
  ja: {
    direction: 'ltr',
    dateFormat: 'YYYY\u5E74MM\u6708DD\u65E5',
    timeFormat: '24h',
    firstDayOfWeek: 0,
    decimalSeparator: '.',
    thousandsSeparator: ',',
    currency: 'JPY',
    currencyPosition: 'before',
    phoneFormat: '+81 XX-XXXX-XXXX',
  },
  de: {
    direction: 'ltr',
    dateFormat: 'DD.MM.YYYY',
    timeFormat: '24h',
    firstDayOfWeek: 1,
    decimalSeparator: ',',
    thousandsSeparator: '.',
    currency: 'EUR',
    currencyPosition: 'after',
    phoneFormat: '+49 XXX XXXXXXXX',
  },
};

// Supported languages
export const supportedLanguages: Language[] = [
  { code: 'en', name: 'English', nativeName: 'English', flag: '\uD83C\uDDFA\uD83C\uDDF8' },
  { code: 'fr', name: 'French', nativeName: 'Fran\u00E7ais', flag: '\uD83C\uDDEB\uD83C\uDDF7' },
  { code: 'es', name: 'Spanish', nativeName: 'Espa\u00F1ol', flag: '\uD83C\uDDEA\uD83C\uDDF8' },
  { code: 'ar', name: 'Arabic', nativeName: '\u0627\u0644\u0639\u0631\u0628\u064A\u0629', flag: '\uD83C\uDDF8\uD83C\uDDE6' },
  { code: 'zh', name: 'Chinese', nativeName: '\u4E2D\u6587', flag: '\uD83C\uDDE8\uD83C\uDDF3' },
  { code: 'hi', name: 'Hindi', nativeName: '\u0939\u093F\u0928\u094D\u0926\u0940', flag: '\uD83C\uDDEE\uD83C\uDDF3' },
  { code: 'pt', name: 'Portuguese', nativeName: 'Portugu\u00EAs', flag: '\uD83C\uDDE7\uD83C\uDDF7' },
  { code: 'ru', name: 'Russian', nativeName: '\u0420\u0443\u0441\u0441\u043A\u0438\u0439', flag: '\uD83C\uDDF7\uD83C\uDDFA' },
  { code: 'ja', name: 'Japanese', nativeName: '\u65E5\u672C\u8A9E', flag: '\uD83C\uDDEF\uD83C\uDDF5' },
  { code: 'de', name: 'German', nativeName: 'Deutsch', flag: '\uD83C\uDDE9\uD83C\uDDEA' },
];

// Voice recognition languages
export const voiceLanguages: Record<string, string[]> = {
  en: ['en-US', 'en-GB', 'en-AU', 'en-CA', 'en-IN'],
  fr: ['fr-FR', 'fr-CA', 'fr-BE', 'fr-CH'],
  es: ['es-ES', 'es-MX', 'es-AR', 'es-CO', 'es-CL'],
  ar: ['ar-SA', 'ar-EG', 'ar-AE', 'ar-MA', 'ar-DZ'],
  zh: ['zh-CN', 'zh-TW', 'zh-HK'],
  hi: ['hi-IN'],
  pt: ['pt-BR', 'pt-PT'],
  ru: ['ru-RU'],
  ja: ['ja-JP'],
  de: ['de-DE', 'de-AT', 'de-CH'],
};

// Initialize i18n
i18n
  .use(initReactI18next)
  .init({
    resources: {
      en: { translation: enTranslations },
      fr: { translation: frTranslations },
      es: { translation: esTranslations },
      ar: { translation: arTranslations },
      zh: { translation: zhTranslations },
      hi: { translation: hiTranslations },
      pt: { translation: ptTranslations },
      ru: { translation: ruTranslations },
      ja: { translation: jaTranslations },
      de: { translation: deTranslations },
    },
    lng: detectUserLanguage(),
    fallbackLng: 'fr',
    debug: false,

    interpolation: {
      escapeValue: false, // React already escapes values
      format: (value: unknown, format: string | undefined, lng: string | undefined): string => {
        const language = lng || 'en';
        if (format === 'number' && typeof value === 'number') {
          return new Intl.NumberFormat(language, {
            minimumFractionDigits: 0,
            maximumFractionDigits: 2,
          }).format(value);
        }
        if (format === 'currency' && typeof value === 'number') {
          const cultural = culturalConfig[language] || culturalConfig.en;
          return new Intl.NumberFormat(language, {
            style: 'currency',
            currency: cultural.currency,
          }).format(value);
        }
        if (format === 'date' && value instanceof Date) {
          return new Intl.DateTimeFormat(language).format(value);
        }
        if (format === 'time' && value instanceof Date) {
          const cultural = culturalConfig[language] || culturalConfig.en;
          return new Intl.DateTimeFormat(language, {
            hour: 'numeric',
            minute: 'numeric',
            hour12: cultural.timeFormat === '12h',
          }).format(value);
        }
        return String(value);
      },
    },

    react: {
      useSuspense: false,
      bindI18n: 'languageChanged loaded',
      bindI18nStore: 'added removed',
      transEmptyNodeValue: '',
      transSupportBasicHtmlNodes: true,
      transKeepBasicHtmlNodesFor: ['br', 'strong', 'i', 'p'],
    },
  });

// Helper functions
export const changeLanguage = (langCode: string): void => {
  i18n.changeLanguage(langCode);
  localStorage.setItem('aidfone_language', langCode);

  // Update document direction for RTL languages
  const cultural = culturalConfig[langCode] || culturalConfig.en;
  document.documentElement.dir = cultural.direction;
  // Use the same locale codes as the prerender output and the SEO meta:
  // 'fr' -> 'fr-CA' (Loi 101 / Quebec), everything else -> as-is.
  // Bots that arrive on a prerendered route see fr-CA from the static HTML;
  // post-toggle clients should agree.
  document.documentElement.lang = langCode === 'fr' ? 'fr-CA' : langCode;

  // Update meta tags for language
  const metaLang = document.querySelector('meta[name="language"]');
  if (metaLang) {
    metaLang.setAttribute('content', langCode);
  }
};

export const getCurrentCulturalSettings = (): CulturalConfig => {
  const currentLang = i18n.language;
  return culturalConfig[currentLang] || culturalConfig.en;
};

export const formatPhoneNumber = (number: string, langCode: string): string => {
  const cultural = culturalConfig[langCode] || culturalConfig.en;
  const format = cultural.phoneFormat;
  // Implementation would format based on the pattern
  return number;
};

export const formatDate = (date: Date, langCode: string): string => {
  return new Intl.DateTimeFormat(langCode).format(date);
};

export const formatCurrency = (amount: number, langCode: string): string => {
  const cultural = culturalConfig[langCode] || culturalConfig.en;
  return new Intl.NumberFormat(langCode, {
    style: 'currency',
    currency: cultural.currency,
  }).format(amount);
};

// Convert numerals for Arabic and Hindi
export const convertNumerals = (number: number | string, langCode: string): string => {
  const cultural = culturalConfig[langCode];
  if (!cultural || !cultural.numerals) return number.toString();

  const numerals = cultural.numerals;
  return number.toString().replace(/[0-9]/g, (digit) => numerals[parseInt(digit, 10)]);
};

export default i18n;
